import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import store from '@/store';
import { Redemption, ResponseGetRedemptions } from '@/api/backplane/BackplaneModels';
import { GetRedemptionsErrHandlers } from '@/api/wps/ErrHandlers';
import { DateRange, ModuleWithDates, ModuleWithLoading } from '@/store/modules/.deprecated/baseModules';
import backplaneClient from '@/api/backplane/BackplaneClient';
import moment from 'moment/moment';
import { config } from '@/utils/config';
import { appModule } from '@/store/modules/moduleApp';
import { codes } from '@/utils/codeConstants';

@Module({
  dynamic: true,
  store,
  name: 'report-redemption',
  namespaced: true,
})
class RedemptionReportModule extends VuexModule {
  /* DATA */

  public nested = new (ModuleWithDates(ModuleWithLoading(Object)))();
  private redemptions: Redemption[] = [];
  private limit: number = config.ReportsLimit;

  /* GETTERS */

  get Redemptions() {
    return this.redemptions;
  }

  /* MUTATIONS */

  @Mutation
  private setRedemptions(redemptions: Redemption[]) {
    this.redemptions = redemptions;
  }

  @Mutation
  private resetRedemptions() {
    this.redemptions = [];
  }

  @Mutation
  protected setFromDate(fromDate: moment.Moment | null) {
    this.nested.setFromDate(fromDate);
  }

  @Mutation
  protected setUntilDate(untilDate: moment.Moment | null) {
    this.nested.setUntilDate(untilDate);
  }

  @Mutation
  protected startLoading() {
    this.nested.startLoading();
  }

  @Mutation
  protected finishLoading() {
    this.nested.finishLoading();
  }

  @Mutation
  private setClearState() {
    this.redemptions = [];
    this.nested.setFromDate(null);
    this.nested.setUntilDate(null);
    this.nested.clearLoading();
    appModule.clearMessages();
  }

  /* ACTIONS */

  @Action
  async changeDates(payload: { username: string; range: DateRange; errs?: GetRedemptionsErrHandlers }) {
    const { username, range, errs } = payload;
    let smthChanged = false;
    const newDates = this.nested.datesToChange(range);

    if (newDates.fromDate !== undefined) {
      smthChanged = true;
      this.setFromDate(newDates.fromDate);
    }
    if (newDates.untilDate !== undefined) {
      smthChanged = true;
      this.setUntilDate(newDates.untilDate);
    }

    if (smthChanged) {
      await this.searchRedemptions({ username, errs });
    }
  }

  @Action
  async searchRedemptions(payload: { username: string; errs?: GetRedemptionsErrHandlers }) {
    appModule.clearMessages();
    try {
      const { username, errs } = payload;
      const fromDate = this.nested.FromDate ? this.nested.FromDate.toISOString() : '';
      const untilDate = this.nested.UntilDate ? this.nested.UntilDate.toISOString() : '';

      if (fromDate == '' && untilDate == '') {
        appModule.addMessageError(codes.REPORT_NO_DATE_SELECTED);
        return;
      }

      let start = 0;
      const redemptions: Redemption[] = [];
      let loop = true;

      while (loop) {
        await this.nested.do.call(this, async () => {
          const query = {
            operator_id: username,
            redeem_date_from: fromDate,
            redeem_date_until: untilDate,
            limit: this.limit,
            start, // offset
          };

          const data: ResponseGetRedemptions = await backplaneClient.getRedemptions(query, errs).toPromise();

          if (data.redemptions) {
            redemptions.push(...data.redemptions);
            start += this.limit;
            if (data.redemptions.length < this.limit) {
              loop = false;
            }
          } else {
            loop = false;
          }
        });
      }

      this.resetRedemptions();
      this.setRedemptions(redemptions);
      if (redemptions.length == 0) {
        appModule.addMessageError(codes.REDEMPTION_REPORT_NO_DATA_FOUND);
      }
    } catch (e) {
      console.error(e);
      appModule.addMessageError(codes.REDEMPTION_REPORT_LOAD_FAILURE);
    }
  }

  @Action
  clearState() {
    this.setClearState();
  }
}

export function getRedemptionReportModule() {
  return getModule(RedemptionReportModule, store);
}
